/// <reference types="vite/client" />

import {
	createLogger,
	type AppEnvironment,
	type ChronologLogger,
	type TraceContext,
} from '@farmart-engineering/chronolog-sdk';
import { createObservedFetch } from './fetch';
import { instrumentAxios } from '@farmart-engineering/chronolog-sdk/axios';
import { createOtlpHttpTransport } from '@farmart-engineering/chronolog-sdk/otel';
import {
	context,
	isSpanContextValid,
	trace,
	type Tracer,
} from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import * as Sentry from '@sentry/react';

type RuntimeEnvironment = AppEnvironment;
type AttributeValue = string | number | boolean;

export const removeDottedPrefixConflicts = (
	attributes: Record<string, AttributeValue>,
): Record<string, AttributeValue> => {
	const keys = Object.keys(attributes);
	return Object.fromEntries(
		Object.entries(attributes).filter(
			([key]) => !keys.some(other => other !== key && other.startsWith(`${key}.`)),
		),
	);
};

export const getDefaultOtlpEndpoints = (
	endpoint = import.meta.env.VITE_OTEL_ENDPOINT,
) => {
	const baseUrl = endpoint?.trim().replace(/\/+$/, '');
	if (!baseUrl) {
		throw new Error(
			'VITE_OTEL_ENDPOINT is required when explicit OTLP endpoints are not configured',
		);
	}

	return {
		logs: `${baseUrl}/v1/logs`,
		traces: `${baseUrl}/v1/traces`,
	};
};

export type DeploymentMetadata = {
	environment: RuntimeEnvironment;
	branch: string;
	target: string;
	targetEnvironment: RuntimeEnvironment;
	targetName: string;
};

export type DeploymentAttributes = {
	'deployment.environment': RuntimeEnvironment;
	'deployment.branch': string;
	'deployment.target': string;
	'deployment.target_environment': RuntimeEnvironment;
	'deployment.target_name': string;
};

type SentryScope = {
	tags?: Record<string, string>;
	contexts?: Record<string, unknown>;
	[key: string]: unknown;
};

type ReactRouterInstrumentation = Pick<
	Parameters<typeof Sentry.reactRouterV6BrowserTracingIntegration>[0],
	| 'useEffect'
	| 'useLocation'
	| 'useNavigationType'
	| 'createRoutesFromChildren'
	| 'matchRoutes'
>;

type SentryConfigWithScope = Record<string, unknown> & {
	environment?: RuntimeEnvironment;
	initialScope?: SentryScope;
	integrations?: unknown[];
	browserTracingOptions?: Record<string, unknown>;
	enableBrowserTracing?: boolean;
	enableReplay?: boolean;
	replayOptions?: Record<string, unknown>;
	reactRouter?: ReactRouterInstrumentation;
};

type OpenTelemetryConfig = {
	logsEndpoint?: string;
	tracesEndpoint?: string;
	headers?: Record<string, string>;
	attributes?: Record<string, AttributeValue>;
};

type LoggerConfig = {
	serviceName?: string;
	version?: string;
	environment?: string;
	opentelemetry?: OpenTelemetryConfig;
	sentry?: SentryConfigWithScope;
};

type LegacyLogOptions = {
	meta?: Record<string, unknown>;
	[key: string]: unknown;
};

type LegacyErrorOptions = LegacyLogOptions & {
	jsonPayload?: Record<string, unknown>;
};

const normalizeEnvironment = (value?: string): RuntimeEnvironment => {
	if (value === 'stage' || value === 'prod') return value;
	return 'dev';
};

const parseDeploymentTarget = (
	value: string | undefined,
	fallbackEnvironment: RuntimeEnvironment,
) => {
	const target = value || `${fallbackEnvironment}/default`;
	const [targetEnvironment = fallbackEnvironment, targetName = 'default'] =
		target.split('/');

	return {
		target,
		targetEnvironment: normalizeEnvironment(targetEnvironment),
		targetName,
	};
};

const runtimeEnvironment = normalizeEnvironment(import.meta.env.VITE_ENV);

export const getDeploymentMetadata = (
	environment: RuntimeEnvironment = runtimeEnvironment,
): DeploymentMetadata => {
	const { target, targetEnvironment, targetName } = parseDeploymentTarget(
		import.meta.env.VITE_DEPLOYMENT_TARGET,
		environment,
	);

	return {
		environment,
		branch: import.meta.env.VITE_DEPLOYMENT_BRANCH || 'local',
		target,
		targetEnvironment,
		targetName,
	};
};

export const toObservabilityAttributes = (
	deployment: DeploymentMetadata,
): DeploymentAttributes => ({
	'deployment.environment': deployment.environment,
	'deployment.branch': deployment.branch,
	'deployment.target': deployment.target,
	'deployment.target_environment': deployment.targetEnvironment,
	'deployment.target_name': deployment.targetName,
});

export const deploymentMetadata = getDeploymentMetadata();
export const deploymentAttributes =
	toObservabilityAttributes(deploymentMetadata);

const withDeploymentAttributes = (
	attributes?: Record<string, AttributeValue>,
) => ({
	...deploymentAttributes,
	...(attributes ?? {}),
});

const withDeploymentTags = (tags?: Record<string, string>) => ({
	...deploymentAttributes,
	...(tags ?? {}),
});

const withDeploymentMeta = (meta?: Record<string, unknown>) => ({
	...deploymentAttributes,
	deployment: deploymentMetadata,
	...(meta ?? {}),
});

const initializeSentry = (
	config: SentryConfigWithScope,
	environment: RuntimeEnvironment,
	serviceName: string,
	version?: string,
) => {
	if (Sentry.isInitialized() || !config.dsn) return;

	const {
		browserTracingOptions,
		enableBrowserTracing = config.tracesSampleRate !== 0,
		enableReplay,
		integrations: configuredIntegrations = [],
		reactRouter,
		replayOptions,
		...sentryOptions
	} = config;
	const integrations = [...configuredIntegrations];

	if (enableBrowserTracing) {
		integrations.push(Sentry.browserTracingIntegration(browserTracingOptions));
	}
	if (enableBrowserTracing && reactRouter) {
		integrations.push(
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect: reactRouter.useEffect,
				useLocation: reactRouter.useLocation,
				useNavigationType: reactRouter.useNavigationType,
				createRoutesFromChildren: reactRouter.createRoutesFromChildren,
				matchRoutes: reactRouter.matchRoutes,
			}),
		);
	}
	if (enableReplay) integrations.push(Sentry.replayIntegration(replayOptions));

	Sentry.init({
		...sentryOptions,
		environment,
		release: version ? `${serviceName}@${version}` : undefined,
		sendDefaultPii: false,
		integrations,
		initialScope: {
			...config.initialScope,
			tags: withDeploymentTags(config.initialScope?.tags),
			contexts: {
				...(config.initialScope?.contexts ?? {}),
				deployment: deploymentMetadata,
			},
		},
	} as Parameters<typeof Sentry.init>[0]);
};

let activeLogger: ChronologLogger = createLogger({
	service: 'farmart-web-frontend-dev',
	environment: 'dev',
	output: 'hidden',
});
// API clients are constructed during module evaluation, before initialize().
// Keep their instrumentation connected to the logger selected at startup.
const instrumentationLogger = new Proxy(activeLogger, {
	get(_target, property) {
		const value = Reflect.get(activeLogger, property);
		return typeof value === 'function' ? value.bind(activeLogger) : value;
	},
});
let tracer: Tracer | undefined;
const instrumentationTracer = new Proxy(
	trace.getTracer('@farmart-engineering/chronolog-sdk'),
	{
		get(target, property) {
			const current = tracer ?? target;
			const value = Reflect.get(current, property);
			return typeof value === 'function' ? value.bind(current) : value;
		},
	},
);
let traceProvider: WebTracerProvider | undefined;
let lifecycleInstalled = false;

export function getChronologTraceContext(): TraceContext | undefined {
	const spanContext = trace.getSpan(context.active())?.spanContext();
	if (!spanContext || !isSpanContextValid(spanContext)) return undefined;
	return {
		traceId: spanContext.traceId,
		spanId: spanContext.spanId,
		traceFlags: spanContext.traceFlags,
	};
}

function installLifecycleFlush(): void {
	if (lifecycleInstalled || typeof window === 'undefined') return;
	lifecycleInstalled = true;
	window.addEventListener('pagehide', () => {
		void activeLogger.flush().catch(() => undefined);
		void traceProvider?.forceFlush().catch(() => undefined);
	});
}

function initializeTelemetry(
	serviceName: string,
	environment: RuntimeEnvironment,
	version: string | undefined,
	config: OpenTelemetryConfig,
): void {
	const defaultEndpoints =
		config.logsEndpoint && config.tracesEndpoint
			? undefined
			: getDefaultOtlpEndpoints();
	const logsEndpoint = config.logsEndpoint || defaultEndpoints?.logs;
	const tracesEndpoint = config.tracesEndpoint || defaultEndpoints?.traces;
	if (!logsEndpoint || !tracesEndpoint) {
		throw new Error('OTLP log and trace endpoints are required');
	}
	const resourceAttributes = removeDottedPrefixConflicts({
		...withDeploymentAttributes(config.attributes),
		'service.name': serviceName,
		'deployment.environment.name': environment,
		...(version ? { 'service.version': version } : {}),
	});

	if (!traceProvider) {
		traceProvider = new WebTracerProvider({
			resource: resourceFromAttributes(resourceAttributes),
			spanProcessors: [
				new BatchSpanProcessor(
					new OTLPTraceExporter({
						url: tracesEndpoint,
						headers: config.headers,
					}),
				),
			],
		});
		traceProvider.register();
	}
	tracer = traceProvider.getTracer(
		'@farmart-engineering/chronolog-sdk',
		version,
	);

	const otlpLogs = createOtlpHttpTransport({
		url: logsEndpoint,
		headers: config.headers,
		service: serviceName,
		environment,
		version,
		resource: resourceAttributes,
		getSpanContext: getChronologTraceContext,
		batchSize: 100,
		flushIntervalMs: 2_000,
		maxBufferedLines: 1_000,
		retries: 2,
	});

	activeLogger = createLogger({
		service: serviceName,
		environment,
		version,
		level: environment === 'dev' ? 'DEBUG' : 'INFO',
		bindings: withDeploymentMeta(),
		output: environment === 'dev' ? 'pretty' : 'hidden',
		transports: [otlpLogs],
		getSpanContext: getChronologTraceContext,
	});
	installLifecycleFlush();
}

export const logger = {
	fetch: createObservedFetch(instrumentationTracer, (message, fields) => {
		activeLogger.error(message, withDefaultFields(fields));
	}),

	defaultAttr: { userId: '' } as Record<string, unknown>,

	initialize: (config: LoggerConfig): Promise<void> => {
		const environment = normalizeEnvironment(config.environment);
		const serviceName = [
			config.serviceName || 'farmart-web',
			'frontend',
			environment,
		].join('-');

		if (config.sentry) {
			initializeSentry(config.sentry, environment, serviceName, config.version);
		}
		try {
			initializeTelemetry(
				serviceName,
				environment,
				config.version,
				config.opentelemetry ?? {},
			);
			if (environment !== 'prod') {
				logger.info(
					`🎯 Logger ready! [${serviceName}] (${environment}) v${config.version}`,
				);
			}
			return Promise.resolve();
		} catch (error) {
			console.error('[logger] initialization failed', error);
			return Promise.resolve();
		}
	},

	attachAxiosInterceptor: (
		axiosInstance: Parameters<typeof instrumentAxios>[0],
		options: Omit<
			Parameters<typeof instrumentAxios>[1],
			'logger' | 'tracer'
		> = {},
	) =>
		instrumentAxios(axiosInstance, {
			...options,
			logger: instrumentationLogger,
			tracer: instrumentationTracer,
			attributes: withDeploymentAttributes(
				options.attributes as Record<string, AttributeValue> | undefined,
			),
			spanAttributes: withDeploymentAttributes(options.spanAttributes),
		}),

	trace: (message: string, fields?: Record<string, unknown>) =>
		activeLogger.trace(message, withDefaultFields(fields)),
	debug: (message: string, fields?: Record<string, unknown>) =>
		activeLogger.debug(message, withDefaultFields(fields)),
	info: (
		message: string,
		metaInfo?: Record<string, unknown>,
		options?: LegacyLogOptions,
	) =>
		activeLogger.info(
			message,
			withDefaultFields({ ...metaInfo, ...(options?.meta ?? {}) }),
		),
	warn: (message: string, fields?: Record<string, unknown>) =>
		activeLogger.warn(message, withDefaultFields(fields)),
	error: (message: string | Error, fields?: Record<string, unknown>) =>
		activeLogger.error(
			typeof message === 'string' ? message : message.message,
			withDefaultFields({
				...fields,
				...(message instanceof Error ? { error: message } : {}),
			}),
		),
	fatal: (message: string, fields?: Record<string, unknown>) =>
		activeLogger.fatal(message, withDefaultFields(fields)),
	log: (message: string, fields?: Record<string, unknown>) =>
		activeLogger.info(message, withDefaultFields(fields)),

	err: (
		message: Error | string,
		payload?: Record<string, unknown>,
		options?: LegacyErrorOptions,
	) => {
		const error = message instanceof Error ? message : new Error(message);
		const fields = withDefaultFields({
			...payload,
			...(options?.jsonPayload ?? {}),
			...(options?.meta ?? {}),
			error,
		});
		if (Sentry.isInitialized()) {
			Sentry.captureException(error, { extra: fields });
		}
		return activeLogger.error(error.message, fields);
	},

	setDefaultAttribute(attr: string, value: unknown) {
		logger.defaultAttr = { ...logger.defaultAttr, [attr]: value };
	},
};

function withDefaultFields(fields?: Record<string, unknown>) {
	return withDeploymentMeta({ ...logger.defaultAttr, ...(fields ?? {}) });
}
